import { DecimalPipe } from '@angular/common';
import type { ElementRef } from '@angular/core';
import { Component, forwardRef, input, model, viewChild } from '@angular/core';
import type { FormCheckboxControl } from '@angular/forms/signals';
import { ONYX_FOCUSABLE } from '../../../directives/onyx-auto-focus.directive';
import { OnyxTooltipDirective } from '../../../directives/onyx-tooltip.directive';
import { OnyxBaseFormControlComponent } from '../../../internal/components/onyx-base-form-control/onyx-base-form-control.component';
import { OnyxErrorComponent } from '../../error/onyx-error/onyx-error.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import { OnyxInputLabelComponent } from '../../labels/onyx-input-label/onyx-input-label.component';
import type { OnyxTooltipContext } from '../../tooltip/interfaces';

@Component({
  selector: 'onyx-checkbox',
  imports: [
    OnyxInputLabelComponent,
    OnyxIconComponent,
    OnyxErrorComponent,
    DecimalPipe,
    OnyxTooltipDirective,
  ],
  templateUrl: './onyx-checkbox.component.html',
  styleUrls: ['./onyx-checkbox.component.scss'],
  host: {
    '(click)': `!disabled() ? switchValue($event) : null; $event.stopPropagation();`,
    '(keydown.space)': `!disabled() ? switchValue($event) : null; $event.stopPropagation()`,
    '(mousedown)': `$event.shiftKey && $event.preventDefault()`,
  },
  providers: [
    {
      provide: ONYX_FOCUSABLE,
      useExisting: forwardRef(() => OnyxCheckboxComponent),
    },
  ],
})
export class OnyxCheckboxComponent
  extends OnyxBaseFormControlComponent
  implements FormCheckboxControl
{
  public readonly checked = model(false);
  public readonly label = input<string>();
  public readonly hint = input<OnyxTooltipContext>();
  public readonly count = input<number | null>(null);
  public readonly indeterminate = input(false);
  public readonly showRequired = input(true);
  public readonly showErrors = input(true);

  private readonly checkboxElementRef =
    viewChild.required<ElementRef<HTMLElement>>('checkboxElement');

  public focus(): void {
    if (this.disabled()) return;

    this.checkboxElementRef().nativeElement.focus();
  }

  protected switchValue(event: Event): void {
    if (this.disabled()) return;

    this.changeValue(!this.checked());
    event.preventDefault();
  }

  protected changeValue(value: boolean): void {
    this.checked.set(value);
    this.touch.emit();
  }
}
